import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTag } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import {
  BundleCard,
  BundleCardFreeTrial,
} from '../viewModel/subscriptionBundle.vm.mapper';
import { Skeleton, Tooltip } from '@mui/material';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { sprinkles } from '@design-system/sprinkles.css';
import clsx from 'clsx';

export const BundleDetailsCard = ({
  name,
  tooltipText,
  onClick,
  ...p
}: BundleCard) => (
  <div
    className={clsx(
      'flex flex-col gap-2 p-4 rounded-[10px] shadow-exercise',
      onClick && 'cursor-pointer',
      sprinkles({
        bg: {
          hover: 'N-10',
          lightMode: 'W-100',
        },
      }),
    )}
    onClick={() => onClick && onClick()}
    data-testid={`subscription-card-${p.testIdSuffix}`}
  >
    <div className="flex items-center gap-4">
      <Typography variant="headline" size="medium">
        {name}
      </Typography>
      {!tooltipText ? null : (
        <Tooltip
          title={
            <div className="flex flex-col gap-4">
              {tooltipText.map((text) => (
                <Typography key={text}>{text}</Typography>
              ))}
            </div>
          }
          placement="right-start"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'black',
                color: 'white',
                fontSize: '12px',
                fontWeight: 500,
                fontFamily: 'inherit',
                padding: '4px 8px',
              },
            },
          }}
        >
          <FontAwesomeIcon icon={faInfoCircle} />
        </Tooltip>
      )}
    </div>
    <EditableDetails {...p} />
  </div>
);

const EditableDetails = ({
  freeTrial,
  showFreeTrial,
  testIdSuffix,
  price,
  submitting,
}: Pick<
  BundleCard,
  'freeTrial' | 'showFreeTrial' | 'price' | 'testIdSuffix' | 'submitting'
>) => {
  if (submitting) {
    return (
      <div
        className="flex flex-col gap-4"
        data-testid={`subscription-card-loader-${testIdSuffix}`}
      >
        <Skeleton variant="rectangular" width="100%" height={30} />
        <Skeleton variant="rectangular" width="100%" height={30} />
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-2 items-center">
        <FontAwesomeIcon icon={faTag} />
        <Typography
          variant="body"
          size="large"
          fontWeight="normal"
          data-testid={`subscription-card-price-${testIdSuffix}`}
        >
          {price}
        </Typography>
      </div>
      <FreeTrial freeTrial={freeTrial} showFreeTrial={showFreeTrial} />
    </>
  );
};

const FreeTrial = ({
  freeTrial,
  showFreeTrial,
}: Pick<BundleCard, 'freeTrial' | 'showFreeTrial'>) => {
  if (!freeTrial || !showFreeTrial) return null;

  return (
    <div className="flex gap-2 items-center">
      <Typography variant="body" size="large" fontWeight="normal">
        7-day free trial for:
      </Typography>
      <Typography variant="body" size="large" fontWeight="normal">
        <FreeTrialLabels freeTrial={freeTrial} />
      </Typography>
    </div>
  );
};

const FreeTrialLabels = ({
  freeTrial,
}: {
  freeTrial?: BundleCardFreeTrial;
}) => {
  if (!freeTrial) return null;

  const labels = [
    {
      title: 'monthly',
      value: freeTrial.monthly.checked,
    },
    {
      title: 'annual',
      value: freeTrial.annual.checked,
    },
  ];

  return (
    <div className="flex gap-4 items-center">
      {labels.map(({ title, value }) => (
        <div className="flex gap-2 items-center" key={title}>
          <FontAwesomeIcon
            icon={value ? faCheck : faXmark}
            className={sprinkles({
              color: value ? 'B-100' : 'N-40',
            })}
          />
          <Typography
            variant="body"
            size="large"
            fontWeight="normal"
            className={sprinkles({
              color: value ? 'B-100' : 'N-40',
            })}
            data-testid={`subscription-free-trial-label-${title}`}
            aria-disabled={!value}
          >
            {title}
          </Typography>
        </div>
      ))}
    </div>
  );
};
